import { RecoveryCase, RecoveryStep } from '../types';
import { INITIAL_RECOVERY_CASE } from '../data/mockState';
import { RECOVERY_STEPS_MASTER } from '../data/recoverySteps';

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
const RECOVERY_STORAGE_KEY = 'idfraud_recovery_case';

function getStoredCase(): RecoveryCase {
  const raw = localStorage.getItem(RECOVERY_STORAGE_KEY);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      return INITIAL_RECOVERY_CASE;
    }
  }
  localStorage.setItem(RECOVERY_STORAGE_KEY, JSON.stringify(INITIAL_RECOVERY_CASE));
  return INITIAL_RECOVERY_CASE;
}

export async function getRecoveryCase(): Promise<RecoveryCase> {
  await delay(300);
  return getStoredCase();
}

export async function updateRecoveryStepStatus(stepNumber: number, isCompleted: boolean, notes?: string): Promise<RecoveryCase> {
  await delay(300);
  const current = getStoredCase();
  const updatedSteps = current.steps.map(step => {
    if (step.stepNumber === stepNumber) {
      return {
        ...step,
        isCompleted,
        completedAt: isCompleted ? new Date().toISOString() : undefined,
        notes: notes !== undefined ? notes : step.notes
      };
    }
    return step;
  });

  const completedCount = updatedSteps.filter(s => s.isCompleted).length;
  const progress = Math.round((completedCount / updatedSteps.length) * 100);

  const updatedCase: RecoveryCase = {
    ...current,
    steps: updatedSteps,
    completedStepsCount: completedCount,
    progressPercentage: progress,
    status: completedCount === updatedSteps.length ? 'resolved' : completedCount > 0 ? 'in_progress' : 'active'
  };

  localStorage.setItem(RECOVERY_STORAGE_KEY, JSON.stringify(updatedCase));
  return updatedCase;
}

export async function startNewEmergencyCase(incidentType: RecoveryCase['incidentType'], customDetails?: string): Promise<RecoveryCase> {
  await delay(500);
  const titles: Record<string, string> = {
    id_stolen: 'Physical ID & Identity Documents Stolen',
    bank_compromised: 'Bank Account & Card Compromise',
    account_opened: 'Fraudulent Account Opened in Your Name',
    email_hacked: 'Primary Email Account Takeover',
    data_breach: 'Major Corporate Data Breach Exposure',
    impersonation: 'Suspected Identity Impersonation',
    unknown: 'Suspected Identity Theft / Unexplained Activity'
  };

  const newCase: RecoveryCase = {
    id: 'case-au-' + Math.floor(1000 + Math.random() * 9000),
    incidentType,
    incidentTitle: titles[incidentType] || 'Identity Incident Response',
    createdAt: new Date().toISOString(),
    status: 'in_progress',
    progressPercentage: 0,
    completedStepsCount: 0,
    totalStepsCount: 8,
    steps: RECOVERY_STEPS_MASTER.map(s => ({ ...s, isCompleted: false })),
    caseNotes: customDetails || ''
  };

  localStorage.setItem(RECOVERY_STORAGE_KEY, JSON.stringify(newCase));
  return newCase;
}
